//import React, { useEffect, useState, useRef } from "react";
import { zodiacsApi } from "../../services/api";
import { Badge, Table, PageHeader, Modal } from "../../components/ui";
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  Filter,
  Check,
  Eye,
  Star,
  Calendar,
  Globe,
  Smile,
  Hash,
  Palette,
  Search,
  X,
} from "lucide-react";


// ── Column-header dropdown (reusable) ────────────────────────────────────────
function HeaderDropdown({ value, onChange, options }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const h = (e) => {
      if (
        btnRef.current &&
        !btnRef.current.contains(e.target) &&
        panelRef.current &&
        !panelRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  const handleToggle = (e) => {
    e.stopPropagation();
    if (!open) {
      const rect = btnRef.current.getBoundingClientRect();
      setPos({
        top: rect.bottom + 8,
        left: rect.left + rect.width / 2,
      });
    }
    setOpen((o) => !o);
  };

  return (
    <>
      <button
        ref={btnRef}
        onClick={handleToggle}
        title="Filter"
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 22,
          height: 22,
          borderRadius: 4,
          cursor: "pointer",
          padding: 0,
          border: value ? "1px solid rgba(15,61,62,0.6)" : "1px solid var(--border)",
          background: value ? "rgba(15,61,62,0.12)" : "transparent",
          color: value ? "#0F3D3E" : "var(--text-muted)",
          transition: "all 0.18s",
        }}
      >
        <Filter
          size={11}
          strokeWidth={2.8}
          style={{
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.18s",
          }}
        />
      </button>

      {open &&
        createPortal(
          <div
            ref={panelRef}
            className="header-dropdown-scroll"
            style={{
              position: "fixed",
              top: pos.top,
              left: pos.left,
              transform: "translateX(-50%)",
              zIndex: 99999,
              minWidth: 150,
              width: "max-content",
              maxHeight: 200,
              background: "#0F3D3E",
              border: "1px solid rgba(255,255,255,0.12)",
              borderRadius: 10,
              boxShadow: "0 12px 32px rgba(0,0,0,0.35)",
              overflowX: "hidden",
              overflowY: "auto",
              paddingRight: 5,
            }}
          >
            <div
              style={{
                position: "absolute",
                top: 8,
                right: 4,
                bottom: 8,
                width: 6,
                borderRadius: 999,
                background: "rgba(255,255,255,0.18)",
                pointerEvents: "none",
              }}
            />
            {options.map((opt) => {
              const isActive = opt.value === value;
              return (
                <div
                  key={String(opt.value)}
                  onClick={() => {
                    onChange(opt.value);
                    setOpen(false);
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive) e.currentTarget.style.background = "rgba(255,255,255,0.10)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = isActive
                      ? "rgba(255,255,255,0.12)"
                      : "transparent";
                  }}
                  style={{
                    padding: "9px 14px",
                    cursor: "pointer",
                    fontSize: 12,
                    fontWeight: isActive ? 700 : 500,
                    color: isActive ? "#fff" : "rgba(255,255,255,0.75)",
                    background: isActive ? "rgba(255,255,255,0.12)" : "transparent",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    transition: "background 0.15s",
                    letterSpacing: "0.3px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {opt.label}
                  {isActive && (
                    <Check size={12} strokeWidth={3} color="#fff" style={{ marginLeft: "auto" }} />
                  )}
                </div>
              );
            })}
          </div>,
          document.body
        )}
    </>
  );
}


// ── Constants ─────────────────────────────────────────────────────────────────
const PERIOD_OPTIONS = [
  { value: "",         label: "All Periods" },
  { value: "TODAY",    label: "Today"       },
  { value: "TOMORROW", label: "Tomorrow"    },
  { value: "WEEKLY",   label: "Weekly"      },
  { value: "MONTHLY",  label: "Monthly"     },
  { value: "YEARLY",   label: "Yearly"      },
];

const MOOD_OPTIONS = [
  { value: "",      label: "All Moods" },
  { value: "CALM",  label: "Calm"      },
  { value: "HAPPY", label: "Happy"     },
];

const PERIOD_COLOR = {
  TODAY:    "var(--teal-300)",
  TOMORROW: "#34d399",
  WEEKLY:   "var(--gold-400)",
  MONTHLY:  "#a78bfa",
  YEARLY:   "var(--orange)",
};


// ── Detail Modal ──────────────────────────────────────────────────────────────
function HoroscopeDetail({ entry, onClose }) {
  if (!entry) return null;
  const sections = [
    ["Personal",  entry.personal],
    ["Health",    entry.health],
    ["Career",    entry.profession],
    ["Love",      entry.emotions],
    ["Travel",    entry.travel],
  ];
  return (
    <Modal open={!!entry} onClose={onClose} title="Horoscope Entry" width={600}>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
        <Badge variant="default">
          <span style={{ color: PERIOD_COLOR[entry.periodType] || "var(--text-primary)" }}>{entry.periodType}</span>
        </Badge>
        <Badge variant="info">{(entry.language || entry.languageCode || "—").toUpperCase()}</Badge>
        {entry.mood && <Badge variant="success">{entry.mood}</Badge>}
        {entry.horoscopeDate && (
          <span style={{ fontSize: 11, color: "var(--text-muted)", alignSelf: "center" }}>
            {new Date(entry.horoscopeDate).toLocaleDateString("en-IN")}
          </span>
        )}
      </div>

      {sections.map(([label, text]) => text ? (
        <div key={label} style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: "var(--teal-300)", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 5 }}>{label}</div>
          <div style={{ fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.6 }}>{text}</div>
        </div>
      ) : null)}

      {entry.luckyNumbers?.numbers && (
        <div style={{ marginTop: 10, padding: "12px 14px", background: "var(--bg-input)", borderRadius: "var(--radius)" }}>
          <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.6px" }}>Luck Info</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {entry.luckyNumbers.numbers.map((n, i) => (
              <span key={i} style={{ background: "rgba(232,184,75,0.15)", color: "var(--gold-400)", padding: "2px 10px", borderRadius: 20, fontSize: 12, display: "inline-flex", alignItems: "center", gap: 5 }}>
                <Hash size={11} strokeWidth={2} /> {n}
              </span>
            ))}
            {entry.luckyColorNames?.colors?.map((c, i) => (
              <span key={`c${i}`} style={{ background: "rgba(77,184,184,0.12)", color: "var(--teal-300)", padding: "2px 10px", borderRadius: 20, fontSize: 12, display: "inline-flex", alignItems: "center", gap: 5 }}>
                <Palette size={11} strokeWidth={2} /> {c}
              </span>
            ))}
          </div>
        </div>
      )}

      {entry.horoscopePercentage && Object.keys(entry.horoscopePercentage).length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.6px" }}>Percentages</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            {Object.entries(entry.horoscopePercentage).map(([k, v]) => (
              <div key={k} style={{ background: "var(--bg-input)", borderRadius: 8, padding: "8px 12px" }}>
                <div style={{ fontSize: 11, color: "var(--text-muted)", textTransform: "capitalize", marginBottom: 4 }}>{k}</div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ flex: 1, height: 4, borderRadius: 2, background: "var(--border)", overflow: "auto" }}>
                    <div style={{ width: `${v}%`, height: "100%", background: "var(--teal-400)", borderRadius: 2 }} />
                  </div>
                  <span style={{ fontSize: 12, color: "var(--teal-300)", fontWeight: 700 }}>{v}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </Modal>
  );
}


// ── Main Component ────────────────────────────────────────────────────────────
export default function Horoscope() {

  const [filters, setFilters] = useState({
    search: "", periodType: "", languageId: "", mood: "", zodiacId: "", page: 0,
  });

  const [data,          setData]          = useState({ content: [], totalPages: 0, totalElements: 0 });
  const [loading,       setLoading]       = useState(true);
  const [selected,      setSelected]      = useState(null);
  const [zodiacOptions, setZodiacOptions] = useState([{ value: "", label: "All Zodiacs" }]);
  const [zodiacIconMap, setZodiacIconMap] = useState({}); // id → iconUrl
  const [langOptions,   setLangOptions]   = useState([{ value: "", label: "All Languages" }]);


  // ── Fetch horoscopes whenever filters change ──────────────────────────────
  useEffect(() => {
    setLoading(true);
    zodiacsApi.getHoroscopes(
      filters.periodType,
      filters.page,
      filters.search,
      filters.languageId,
      filters.zodiacId,
      filters.mood
    )
      .then(r => {
        const d = r.data.data;
        setData(d);

        const seen = new Map();
        (d.content || []).forEach(h => {
          if (h.languageId && h.language && !seen.has(h.languageId))
            seen.set(h.languageId, h.language);
        });
        if (seen.size > 0) {
          setLangOptions(prev => {
            const merged = new Map(prev.filter(o => o.value).map(o => [o.value, o.label]));
            seen.forEach((name, id) => merged.set(id, name));
            return [
              { value: "", label: "All Languages" },
              ...[...merged.entries()].map(([id, name]) => ({ value: id, label: name })),
            ];
          });
        }
      })
      .catch(() => setData({ content: [], totalPages: 0, totalElements: 0 }))
      .finally(() => setLoading(false));
  }, [filters]);


  // ── Load zodiac list once on mount ────────────────────────────────────────
  useEffect(() => {
    zodiacsApi.getAll(0, 100)
      .then(r => {
        const list = r?.data?.data?.content || r?.data?.data || [];
        const opts = [{ value: "", label: "All Zodiacs" }];
        const iconMap = {};
        list.forEach(z => {
          const name = z.name || z.zodiacName || z.sign || z.englishName || z.title || null;
          if (z.id && name) {
            opts.push({ value: z.id, label: name });
            if (z.iconUrl) iconMap[z.id] = z.iconUrl;
          }
        });
        setZodiacOptions(opts);
        setZodiacIconMap(iconMap);
      })
      .catch(() => {});
  }, []);


  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleSearch = (e) => {
    e.preventDefault();
    setFilters(f => ({ ...f, page: 0 }));
  };

  const handleClear = () => {
    setFilters({ search: "", periodType: "", languageId: "", mood: "", zodiacId: "", page: 0 });
  };


  // ── Table columns ──────────────────────────────────────────────────────────
  const columns = [
    {
      key: "id", label: "Sr.",
      render: (v) => {
        const index = data.content.findIndex(item => item.id === v);
        return (
          <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text-muted)", minWidth: 28, display: "inline-block" }}>
            {filters.page * 20 + index + 1}
          </span>
        );
      },
    },

    {
      key: "createdAt", label: "Date",
      render: (v) => v
        ? <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)", whiteSpace: "nowrap" }}>
            {new Date(v).toLocaleDateString("en-IN")}
          </span>
        : "—",
    },

    {
      key: "zodiacId",
      label: (
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span>Zodiac</span>
          <HeaderDropdown
            value={filters.zodiacId}
            onChange={(v) => setFilters(f => ({ ...f, zodiacId: v, page: 0 }))}
            options={zodiacOptions}
          />
        </div>
      ),
      render: (v) => {
        const name = zodiacOptions.find(o => o.value === v)?.label;
        const iconUrl = zodiacIconMap[v];
        return name ? (
          <span style={{ fontSize: 12, fontWeight: 600, color: "var(--text-primary)", display: "inline-flex", alignItems: "center", gap: 6 }}>
            {iconUrl ? (
              <img
                src={iconUrl}
                alt={name}
                style={{ width: 20, height: 20, objectFit: "contain", borderRadius: 4, flexShrink: 0 }}
                onError={e => e.target.style.display = "none"}
              />
            ) : (
              <Star size={14} strokeWidth={2} color="var(--teal-300)" style={{ flexShrink: 0 }} />
            )}
            {name}
          </span>
        ) : (
          <span style={{ fontSize: 11, fontFamily: "monospace", color: "var(--text-muted)" }}>
            {v?.slice(-8) || "—"}
          </span>
        );
      },
    },

    {
      key: "periodType",
      label: (
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span>Period</span>
          <HeaderDropdown
            value={filters.periodType}
            onChange={(v) => setFilters(f => ({ ...f, periodType: v, page: 0 }))}
            options={PERIOD_OPTIONS}
          />
        </div>
      ),
      render: (v) => (
        <Badge variant="default">
          <span style={{ color: PERIOD_COLOR[v] || "var(--text-primary)" }}>
            {v ? v.charAt(0).toUpperCase() + v.slice(1).toLowerCase() : "—"}
          </span>
        </Badge>
      ),
    },

    {
      key: "language",
      label: (
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span>Language</span>
          <HeaderDropdown
            value={filters.languageId}
            onChange={(v) => setFilters(f => ({ ...f, languageId: v, page: 0 }))}
            options={langOptions}
          />
        </div>
      ),
      render: (v, row) => (
        <Badge variant="info">{(v || row.languageCode || "—").toUpperCase()}</Badge>
      ),
    },

    {
      key: "mood",
      label: (
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span>Mood</span>
          <HeaderDropdown
            value={filters.mood}
            onChange={(v) => setFilters(f => ({ ...f, mood: v, page: 0 }))}
            options={MOOD_OPTIONS}
          />
        </div>
      ),
      render: (v) => v
        ? (
          <Badge variant="success">
            <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
              <Smile size={12} strokeWidth={2} /> {v}
            </span>
          </Badge>
        )
        : <span style={{ color: "var(--text-muted)", fontSize: 12 }}>—</span>,
    },

    {
      key: "personal", label: "Preview",
      render: (v) => v
        ? <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>{v.slice(0, 80)}{v.length > 80 ? "…" : ""}</span>
        : <span style={{ color: "var(--text-muted)", fontSize: 12 }}>—</span>,
    },

    {
      key: "_details", label: "Details",
      render: (_, row) => (
        <button
          onClick={() => setSelected(row)}
          title="View Details"
          style={{
            background: "none", border: "none", cursor: "pointer",
            color: "var(--text-muted)", padding: 4, borderRadius: 6,
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            transition: "color 0.15s",
          }}
          onMouseEnter={e => e.currentTarget.style.color = "#0F3D3E"}
          onMouseLeave={e => e.currentTarget.style.color = "var(--text-muted)"}
        >
          <Eye size={17} strokeWidth={2} />
        </button>
      ),
    },
  ];

  const hasFilters = filters.search || filters.periodType || filters.languageId || filters.mood || filters.zodiacId;


  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }

        .header-dropdown-scroll {
          scrollbar-width: auto;
          scrollbar-color: rgba(255,255,255,0.7) rgba(255,255,255,0.14);
          scrollbar-gutter: stable;
        }

        .header-dropdown-scroll::-webkit-scrollbar {
          width: 12px;
        }

        .header-dropdown-scroll::-webkit-scrollbar-track {
          background: rgba(255,255,255,0.14);
          border-radius: 10px;
        }

        .header-dropdown-scroll::-webkit-scrollbar-thumb {
          background: rgba(255,255,255,0.72);
          border-radius: 10px;
          border: 2px solid #0F3D3E;
          min-height: 28px;
        }

        .header-dropdown-scroll::-webkit-scrollbar-thumb:hover {
          background: rgba(255,255,255,0.9);
        }
      `}</style>

      <PageHeader title="Horoscope Cache" subtitle="Browse all cached horoscope content" />

      {/* Search bar */}
      <form onSubmit={handleSearch} style={{ display: "flex", gap: 10, marginBottom: 20 }}>
        <div style={{ position: "relative" }}>
          <Search
            size={14}
            strokeWidth={2}
            style={{
              position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)",
              color: "var(--text-muted)", pointerEvents: "none",
            }}
          />
          <input
            placeholder="Search by zodiac name or mood…"
            value={filters.search}
            onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
            style={{
              width: 260, padding: "9px 14px 9px 30px", borderRadius: "var(--radius)",
              border: "1px solid var(--border)", background: "var(--bg-input)",
              color: "var(--text-primary)", fontSize: 13, fontFamily: "var(--font-body)",
              outline: "none",
            }}
          />
        </div>
        <button type="submit" style={{
          padding: "9px 20px", borderRadius: "var(--radius)", border: "none",
          background: "#0F3D3E", color: "#fff", fontSize: 13, fontWeight: 600,
          cursor: "pointer", fontFamily: "var(--font-body)",
        }}>Search</button>
        <button type="button" onClick={handleClear} style={{
          padding: "9px 16px", borderRadius: "var(--radius)",
          border: "1px solid var(--border)", background: "transparent",
          color: "var(--text-secondary)", fontSize: 13, cursor: "pointer",
          fontFamily: "var(--font-body)",
        }}>Clear</button>
      </form>

      {/* Table */}
      <div
        style={{
          background: "var(--bg-card)",
          border: "1px solid var(--border)",
          borderRadius: "var(--radius-lg)",
          overflowX: "hidden",
          overflowY: "visible",
          position: "relative",
          zIndex: 1,
        }}
      >

        {/* Header bar */}
        <div style={{ padding: "14px 20px", borderBottom: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
          <span style={{ fontSize: 13, color: "var(--text-secondary)" }}>{data.totalElements} entries</span>
          {hasFilters && (
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {filters.search && (
                <span style={{ fontSize: 11, background: "rgba(15,61,62,0.1)", color: "#0F3D3E", borderRadius: 20, padding: "2px 10px", fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 4 }}>
                  <Search size={10} strokeWidth={2.5} /> {filters.search}
                </span>
              )}
              {filters.zodiacId && (
                <span style={{ fontSize: 11, background: "rgba(15,61,62,0.1)", color: "#0F3D3E", borderRadius: 20, padding: "2px 10px", fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 4 }}>
                  {zodiacIconMap[filters.zodiacId] ? (
                    <img src={zodiacIconMap[filters.zodiacId]} alt="" style={{ width: 12, height: 12, objectFit: "contain" }} />
                  ) : (
                    <Star size={10} strokeWidth={2.5} />
                  )}
                  {zodiacOptions.find(o => o.value === filters.zodiacId)?.label || filters.zodiacId}
                </span>
              )}
              {filters.periodType && (
                <span style={{ fontSize: 11, background: "rgba(15,61,62,0.1)", color: "#0F3D3E", borderRadius: 20, padding: "2px 10px", fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 4 }}>
                  <Calendar size={10} strokeWidth={2.5} /> {filters.periodType}
                </span>
              )}
              {filters.languageId && (
                <span style={{ fontSize: 11, background: "rgba(15,61,62,0.1)", color: "#0F3D3E", borderRadius: 20, padding: "2px 10px", fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 4 }}>
                  <Globe size={10} strokeWidth={2.5} /> {langOptions.find(o => o.value === filters.languageId)?.label || filters.languageId}
                </span>
              )}
              {filters.mood && (
                <span style={{ fontSize: 11, background: "rgba(15,61,62,0.1)", color: "#0F3D3E", borderRadius: 20, padding: "2px 10px", fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 4 }}>
                  <Smile size={10} strokeWidth={2.5} /> {filters.mood}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Table body */}
        {loading
          ? <div style={{ padding: 40, textAlign: "center", color: "var(--text-muted)" }}>
              <div style={{ width: 32, height: 32, margin: "0 auto 10px", border: "3px solid var(--border)", borderTop: "3px solid #0F3D3E", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
              Loading…
            </div>
          : <Table columns={columns} data={data.content || []} emptyText="No horoscope entries found" />
        }

        {/* Pagination */}
        <div style={{ padding: "14px 20px", borderTop: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
          {(() => {
            const totalPages = data.totalPages;
            if (totalPages <= 1) return null;

            const getPages = () => {
              if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i);
              const pages = [];
              if (filters.page <= 3) {
                pages.push(0, 1, 2, 3, 4, "...", totalPages - 1);
              } else if (filters.page >= totalPages - 4) {
                pages.push(0, "...", totalPages - 5, totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1);
              } else {
                pages.push(0, "...", filters.page - 1, filters.page, filters.page + 1, "...", totalPages - 1);
              }
              return pages;
            };

            const btnStyle = (isActive, isDisabled) => ({
              minWidth: 34, height: 34, padding: "0 10px",
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              border: isActive ? "none" : "1px solid var(--border)",
              borderRadius: 8,
              background: isActive ? "#0F3D3E" : "transparent",
              color: isActive ? "#fff" : isDisabled ? "var(--text-muted)" : "var(--text-primary)",
              fontSize: 13, fontWeight: isActive ? 700 : 500,
              cursor: isDisabled ? "not-allowed" : "pointer",
              opacity: isDisabled ? 0.45 : 1,
              transition: "all 0.15s",
              fontFamily: "var(--font-body)",
            });

            return (
              <>
                <button
                  disabled={filters.page === 0}
                  onClick={() => setFilters(f => ({ ...f, page: f.page - 1 }))}
                  style={btnStyle(false, filters.page === 0)}
                >← Prev</button>

                {getPages().map((p, i) =>
                  p === "..." ? (
                    <span key={`dots-${i}`} style={{ padding: "0 4px", color: "var(--text-muted)", fontSize: 14, userSelect: "none" }}>…</span>
                  ) : (
                    <button
                      key={p}
                      onClick={() => setFilters(f => ({ ...f, page: p }))}
                      style={btnStyle(filters.page === p, false)}
                      onMouseEnter={e => { if (filters.page !== p) e.currentTarget.style.background = "rgba(15,61,62,0.08)"; }}
                      onMouseLeave={e => { if (filters.page !== p) e.currentTarget.style.background = "transparent"; }}
                    >
                      {p + 1}
                    </button>
                  )
                )}

                <button
                  disabled={filters.page >= totalPages - 1}
                  onClick={() => setFilters(f => ({ ...f, page: f.page + 1 }))}
                  style={btnStyle(false, filters.page >= totalPages - 1)}
                >Next →</button>
              </>
            );
          })()}
        </div>
      </div>

      <HoroscopeDetail entry={selected} onClose={() => setSelected(null)} />
    </div>
  );
}